import { Dialog } from '@headlessui/react';
import { Palette, Pencil, Ruler, Shirt, X } from 'lucide-react';

export type OrderProduct = {
  id: number;
  garment_id: number;
  color_id: number;
  size_id: number;
  garment?: { id: number; name: string };
  color?: { id: number; name: string; hex_code: string };
  size?: { id: number; name: string };
};

export type OrderCustomization = {
  order_id: string;
  option_id: number;
  value: string;
  option?: {
    id?: number;
    name: string;
    value_type?: string;
  };
};

interface OrderDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  product?: OrderProduct | null;
  customizations?: OrderCustomization[];
}

function isActiveCustomization(customization: OrderCustomization) {
  const value = customization.value?.trim();
  return Boolean(value) && value !== 'false' && value !== '0';
}

export default function OrderDetailsModal({ isOpen, onClose, product, customizations = [] }: OrderDetailsModalProps) {
  const garmentName = product?.garment?.name ?? 'Not available';
  const colorName = product?.color?.name ?? 'Not available';
  const colorHex = product?.color?.hex_code;
  const sizeName = product?.size?.name ?? 'Not available';
  const activeCustomizations = customizations.filter(isActiveCustomization);

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-[99999]">
      <div className="fixed inset-0 bg-black/30 backdrop-blur-sm" aria-hidden="true" />

      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="w-full max-w-lg bg-white rounded-lg shadow-xl border border-gray-100">
          <div className="p-5 space-y-4">
            <div className="flex items-start justify-between gap-4">
              <div>
                <Dialog.Title className="text-xl sm:text-2xl font-semibold text-gray-700">
                  Pedido actual
                </Dialog.Title>
                <p className="text-sm text-gray-500 mt-1">
                  Ver los detalles de tu pedido actual de un vistazo
                </p>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="bg-red-50 rounded-lg p-2 hover:bg-red-100 transition-colors duration-300"
                aria-label="Close"
              >
                <X className="w-5 h-5 text-red-500" />
              </button>
            </div>

            {product ? (
              <div className="space-y-3">
                <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
                  <div className="flex items-center gap-3">
                    <div className="bg-red-50 rounded-lg p-2 h-fit">
                      <Shirt className="w-5 h-5 text-red-500" />
                    </div>
                    <div>
                      <p className="text-xs font-light text-gray-500 uppercase mb-1">Prenda</p>
                      <p className="text-sm font-medium text-gray-800">{garmentName}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
                  <div className="flex items-center gap-3">
                    <div className="bg-red-50 rounded-lg p-2 h-fit">
                      <Palette className="w-5 h-5 text-red-500" />
                    </div>
                    <div>
                      <p className="text-xs font-light text-gray-500 uppercase mb-1">Color</p>
                      <div className="flex items-center gap-2">
                        {colorHex && (
                          <span
                            className="w-4 h-4 rounded-full border border-gray-200 shrink-0"
                            style={{ backgroundColor: colorHex }}
                          />
                        )}
                        <p className="text-sm font-medium text-gray-800">{colorName}</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
                  <div className="flex items-center gap-3">
                    <div className="bg-red-50 rounded-lg p-2 h-fit">
                      <Ruler className="w-5 h-5 text-red-500" />
                    </div>
                    <div>
                      <p className="text-xs font-light text-gray-500 uppercase mb-1">Talla</p>
                      <p className="text-sm font-medium text-gray-800">{sizeName}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
                  <div className="flex items-start gap-3">
                    <div className="bg-red-50 rounded-lg p-2 h-fit">
                      <Pencil className="w-5 h-5 text-red-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-light text-gray-500 uppercase mb-2">Personalizaciones</p>
                      {activeCustomizations.length > 0 ? (
                        <div className="space-y-2">
                          {activeCustomizations.map((customization) => (
                            <div
                              key={customization.option_id}
                              className="flex items-center justify-between gap-3"
                            >
                              <span className="text-sm text-gray-600 truncate">
                                {customization.option?.name ?? `Option ${customization.option_id}`}
                              </span>
                              <span className="text-sm font-medium text-gray-800 shrink-0">
                                {customization.value}
                              </span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm font-medium text-gray-800">No hay personalizaciones</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
                <p className="text-sm font-medium text-gray-800">No hay detalles de la prenda</p>
                <span className="text-xs text-slate-400 font-semibold">
                  Tu pedido actual no tiene una prenda asignada todavía
                </span>
              </div>
            )}

            <div className="flex justify-end pt-1">
              <button
                type="button"
                onClick={onClose}
                className="px-3 py-1.5 rounded-lg text-sm transition-all relative flex items-center gap-2 shadow-sm hover:shadow-md bg-red-500 hover:bg-red-800 text-white font-semibold"
              >
                Cerrar
              </button>
            </div>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}
